import { useTranslation } from 'react-i18next';
import { useInView } from '@/hooks/useInView';

export default function About() {
  const { t } = useTranslation();
  const { ref, inView } = useInView<HTMLDivElement>(0.2);

  return (
    <section id="about" className="w-full bg-foreground-900 py-24 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 tech-grid-dark opacity-20" />
      <div className="absolute -top-32 right-1/3 w-96 h-96 bg-accent-500/15 rounded-full blur-[100px]" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div
            className={`inline-flex px-3 py-1.5 rounded-md bg-secondary-100 text-foreground-800 text-xs font-mono-tech tracking-widest uppercase mb-8 ${inView ? 'animate-fade-up' : 'opacity-0'}`}
          >
            {t('about.tag')}
          </div>
          <h2
            className={`font-heading text-background-50 text-4xl md:text-6xl lg:text-7xl leading-[1.05] font-semibold tracking-tight ${inView ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.15s' }}
          >
            {t('about.title')}
          </h2>
          <p
            className={`mt-8 text-base md:text-lg text-background-200/80 leading-relaxed max-w-2xl ${inView ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.3s' }}
          >
            {t('about.desc')}
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div
            className={`relative aspect-[4/3] rounded-2xl overflow-hidden border border-background-50/10 ${inView ? 'animate-slide-left' : 'opacity-0'}`}
            style={{ animationDelay: '0.4s' }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Editorial%20isometric%20abstract%20four-phase%20blockchain%20framework%20diagram%20with%20warm%20cream%20paper%20background%2C%20minimal%20architectural%20line%20illustration%2C%20subtle%20lime%20green%20highlight%20accents%2C%20premium%20fintech%20magazine%20aesthetic%2C%20clean%20negative%20space%2C%20soft%20amber%20warm%20tones%2C%20professional%20studio%20composition&width=1200&height=900&seq=about-framework-01&orientation=landscape"
              alt={t('about.number.label')}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-background-50 text-foreground-950 text-[10px] font-mono-tech tracking-widest rounded-full">
              FRAMEWORK · 40°
            </div>
          </div>

          <div className={inView ? 'animate-fade-up' : 'opacity-0'} style={{ animationDelay: '0.5s' }}>
            <div className="font-heading text-primary-500 text-[140px] md:text-[180px] leading-none font-semibold tracking-tighter">
              {t('about.number')}
            </div>
            <div className="mt-4 text-xs font-mono-tech tracking-[0.3em] uppercase text-background-100/60">
              {t('about.number.label')}
            </div>
            <p className="mt-6 text-base md:text-lg text-background-200/80 leading-relaxed max-w-lg">
              {t('about.number.desc')}
            </p>
            <a
              href="#services"
              className="mt-10 inline-flex items-center gap-3 px-6 py-3 bg-background-50 hover:bg-primary-500 text-foreground-950 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer group"
            >
              {t('about.cta')}
              <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}